/**
 * TanStack Query のクエリキーを一元管理するファイル
 *
 * 命名規則:
 *   - 一覧（複数形）: opportunities / inquiries
 *   - 詳細・サブリソース（単数形）: opportunity / inquiry
 */

export const queryKeys = {
  // ===========================
  // アカウント
  // ===========================
  account: ['account'] as const,

  // ===========================
  // マスタデータ
  // ===========================
  taskStatuses: ['task-statuses'] as const,
  opportunityStatuses: ['opportunity-statuses'] as const,
  allTags: ['all-tags'] as const,
  closeReasons: ['close-reasons'] as const,
  shiftServices: ['shift-services'] as const,
  memberMonthlySales: ['member-monthly-sales'] as const,
  divisions: (filters?: { isEnabled?: boolean; isAssignable?: boolean }) => ['divisions', filters] as const,
  inquiryAssignStatuses: ['inquiry-assign-statuses'] as const,
  salesforceCustomers: (q: string) => ['salesforce-customers', q] as const,

  // ===========================
  // オポチュニティ一覧
  // ===========================
  opportunities: {
    all: () => ['opportunities'] as const,
    list: (params: { all?: boolean; salesforceProjectMemberTypeId?: number; offset?: number; limit?: number }) =>
      ['opportunities', params] as const,
    savedQueries: () => ['opportunity-saved-queries'] as const,
  },

  // ===========================
  // オポチュニティ詳細・サブリソース
  // ===========================
  opportunity: {
    all: (id: number) => ['opportunity', id] as const,
    detail: (id: number) => ['opportunity', id, 'detail'] as const,
    actionLogs: (id: number, params: { offset?: number; limit?: number }) =>
      ['opportunity', id, 'action-logs', params] as const,
    monthlySales: (id: number, params: { period?: string; offset?: number; limit?: number }) =>
      ['opportunity', id, 'monthly-sales', params] as const,
    tasks: (id: number) => ['opportunity', id, 'tasks'] as const,
    tasksList: (id: number, params: { taskStatusId?: number; memberId?: number }) =>
      ['opportunity', id, 'tasks', params] as const,
    comments: (id: number) => ['opportunity', id, 'comments'] as const,
    commentMarkers: (id: number) => ['opportunity', id, 'comment-markers'] as const,
    tags: (id: number | undefined) => ['opportunity', id, 'tags'] as const,
    summary: (params: { startAt?: string }) => ['opportunity', 'summary', params] as const,
    incentives: (memberId: number | undefined, period?: string) =>
      ['opportunity', 'incentives', memberId, period] as const,
    incentiveAccepts: (memberId: number | undefined) => ['opportunity', 'incentive-accepts', memberId] as const,
  },

  // ===========================
  // オポチュニティ関連（別ドメイン）
  // ===========================
  sharableOpportunities: (params: { onlyRelease?: boolean; offset?: number; limit?: number | null }) =>
    ['sharable-opportunities', params] as const,
  sharableOpportunityDetail: (id: number) => ['sharable-opportunity-detail', id] as const,
  myTasks: ['my-tasks'] as const,
  myTasksList: (params?: { offset?: number; limit?: number }) => ['my-tasks', params] as const,
  chatMembers: (projectId: number) => ['chat-members', projectId] as const,

  // ===========================
  // Salesforce 連携（オポチュニティ）
  // ===========================
  salesforceProjects: (memberTypeId: number) => ['salesforce-projects', memberTypeId] as const,
  salesforceProjectMembers: (projectId: number | null | undefined, params: { offset?: number; limit?: number }) =>
    ['salesforce', projectId, 'members', params] as const,

  // ===========================
  // 引合一覧
  // ===========================
  inquiries: {
    all: () => ['inquiries'] as const,
    savedQueries: () => ['inquiry-saved-queries'] as const,
  },

  // ===========================
  // 引合詳細・サブリソース
  // ===========================
  inquiry: {
    actionLogs: (id: number) => ['inquiry', id, 'action-logs'] as const,
    assigns: (id: number) => ['inquiry', id, 'assigns'] as const,
    coordinations: (id: number) => ['inquiry', id, 'coordinations'] as const,
    relatedDivisions: (id: number) => ['inquiry', id, 'related-divisions'] as const,
    sales: (id: number) => ['inquiry', id, 'sales'] as const,
    assignMembers: {
      all: () => ['inquiry-assign-members'] as const,
      list: (params: { inquiryId?: number | null; startOn?: string | null; endOn?: string | null }) =>
        ['inquiry-assign-members', params] as const,
    },
  },

  // ===========================
  // 引合関連（別ドメイン）
  // ===========================
  roles: ['roles'] as const,
  inquiryRoleMembers: (params: {
    isSales?: boolean
    isSmg?: boolean
    isManager?: boolean
    offset?: number
    limit?: number
  }) => ['inquiry-role-members', params] as const,

  // ===========================
  // Salesforce 連携（引合）
  // ===========================
  contacts: (salesforceCustomerId?: number) => ['contacts', salesforceCustomerId] as const,
  salesforceDeals: (salesforceCustomerId?: number) =>
    salesforceCustomerId !== undefined
      ? (['salesforce-deals', salesforceCustomerId] as const)
      : (['salesforce-deals'] as const),
  salesforceDealAnos: (dealId?: number) =>
    dealId !== undefined ? (['salesforce-deal-anos', dealId] as const) : (['salesforce-deal-anos'] as const),
}
